import { useCallback, useEffect, useState } from 'react';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from './serverErrors';

/**
 * The ATMS locales the portal web service returns at /locales, and how they are labelled. Used by
 * the base rates page, LocaleSelect and the pricing model pages.
 */

/** An ATMS locale; `code` is the BCP-47 tag as stored, which pricing matches on. */
export interface Locale {
  code: string;
  name: string;
}

export const LOCALES_PATH = '/locales';

interface LocalesClient {
  httpGet: (path: string) => Promise<unknown>;
}

/** A null locale means any locale. */
export const formatLocale = (code: string | null): string => (code === null ? 'Any' : code);

/** "name (code)" when the locale is known, otherwise the code alone. */
export const localeLabel = (code: string | null, locale?: Locale): string => {
  if (code === null || !locale) {
    return formatLocale(code);
  }
  return `${locale.name} (${code})`;
};

// The locales rarely change, so each client fetches them once and every page shares the result.
const pendingByClient = new WeakMap<LocalesClient, Promise<Locale[]>>();

/** Resolves with the locales, fetching them only on first use; a failed fetch is retried next time. */
export const fetchLocales = (apiClient: LocalesClient): Promise<Locale[]> => {
  const cached = pendingByClient.get(apiClient);
  if (cached) {
    return cached;
  }
  const pending = apiClient.httpGet(LOCALES_PATH) as Promise<Locale[]>;
  pendingByClient.set(apiClient, pending);
  pending.catch(() => {
    if (pendingByClient.get(apiClient) === pending) {
      pendingByClient.delete(apiClient);
    }
  });
  return pending;
};

export interface LocalesState {
  // Null until the locales have loaded.
  locales: Locale[] | null;
  errors: string[];
  reload: () => void;
}

/** The shared locales; `describeError` must be stable, e.g. defined at module level. */
export const useLocales = (
  apiClient: LocalesClient,
  describeError: (error: ApiError) => string[]
): LocalesState => {
  const [locales, setLocales] = useState<Locale[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const reload = useCallback((): void => {
    setErrors([]);
    fetchLocales(apiClient)
      .then(setLocales)
      .catch((error: ApiError) => setErrors(describeError(error)));
  }, [apiClient, describeError]);

  useEffect(reload, [reload]);

  return { locales, errors, reload };
};
